import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import TailwindClearableTimerInput from 'widgets/editors/tailwind-clearable-timer-input';
import TailwindMultiEventTypeEditor from 'widgets/bussiness/multiple-selector/multi-event-type-editor-tw';
import TailwindCategoryFormEditor from 'widgets/bussiness/category-form-editor-tw';
import TailwindMachineRelatedEditor from 'widgets/bussiness/machine-related-editor-tw';
import TailwindMultipleNmrClustersEditor from 'widgets/bussiness/multiple-selector/multiple-nmr-cluster-editor-tw';
import TailwindMultipleLocationEditor from 'widgets/bussiness/multiple-selector/multiple-location-editor-tw';
import TailwindMultipleRegionEditor from 'widgets/bussiness/multiple-selector/multiple-region-editor-tw';
import TailwindDeliveryEditor from 'widgets/bussiness/delivery-method-editor-tw';
import TailwindMultiplePriorityEditor from 'widgets/bussiness/multiple-selector/multiple-priority-editor-tw';
import TailwindMultipleUsersEditor from 'widgets/bussiness/multiple-selector/multiple-user-editor-tw';
import TailwindMultiplePillarsEditor from 'widgets/bussiness/multiple-selector/multiple-pillar-editor-tw';
import TailwindMultipleProfessionsEditor from 'widgets/bussiness/multiple-selector/multiple-professions-editor-tw';
import TailwindMultipleEventStatusEditor from 'widgets/bussiness/multiple-selector/multiple-event-status-editor-tw';
import { DateInput } from 'widgets/form/dateInput';
import { ItemReference } from 'stores/dataStore';
import { Category, NMClusterDto } from 'stores/configuration/events-n-requests/non-machine-related/clusters-store';
import { LocationDto } from 'stores/configuration/locations/locations-store';
import { RegionDto } from 'stores/configuration/locations/regions-store';
import { PriorityDto } from 'stores/configuration/events-n-requests/priorities-store';
import { UserDto } from 'stores/users/users-store';
import { PillarDto } from 'stores/configuration/profiles/pillars-store';
import { ProfessionDto } from 'stores/configuration/profiles/profession-roles-store';
import { EventStatus } from 'stores/events/events-store';

const EventListFilters = ({ onClose, onSave, values }) => {
  const { t } = useTranslation();
  const [category, setCategory] = useState<Category>(values['category'] || null);
  const [machineRelated, setMachineRelated] = useState<ItemReference>(values['machineRelated'] || null);
  const [clusterList, setClusterList] = useState<NMClusterDto[]>(values['nmrCluster'] || []);
  const [requestingLocationList, setRequestingLocationList] = useState<LocationDto[]>(values['requests'] || []);
  const [requestRegionLocationList, setRequestRegionLocationList] = useState<RegionDto[]>(values['extendedRequests'] || []);
  const [locationList, setLocationList] = useState<LocationDto[]>(values['eventLocation'] || []);
  const [delivery, setDelivery] = useState<ItemReference>(values['delivery'] || null);
  const [startDate, setStartDate] = useState<string>(values['startDate'] || '');
  const [endDate, setEndDate] = useState<string>(values['endtDate'] || '');
  const [priorityList, setPriorityList] = useState<PriorityDto[]>(values['priority'] || []);
  const [instructorsNameList, setInstructorsNameList] = useState<UserDto[]>(values['instructors'] || []);
  const [instructorPillarList, setInstructorPillarList] = useState<PillarDto[]>(values['instructorsPillars'] || []);
  const [rolesList, setRolesList] = useState<ProfessionDto[]>(values['profession'] || []);
  const [eventStatus, setEventStatus] = useState<EventStatus[]>(values['EventStatus'] || []);
  const [eventTypeList, setEventTypeList] = useState<ItemReference[]>(values['eventType'] || []);
  const [title, setTitle] = useState<string>(values['title'] || '');
  const [friendlyId, setFriendlyId] = useState<string>(values['friendlyId'] || '');
  const [filters, setFilters] = useState<any[]>(values || []);

  const containerRef = useRef<HTMLDivElement>(null);
  const [containerHeight, setContainerHeight] = useState<number | 'auto'>('auto');

  const [dropdownStates, setDropdownStates] = useState<{ [key: string]: boolean }>({
    EventStatus: false,
    profession: false,
    instructorsPillars: false,
    instructors: false,
    delivery: false,
    extendedRequests: false,
    requests: false,
    nmrCluster: false,
    machineRelated: false,
    category: false,
    eventType: false
  });

  const handleDropdownOpen = (key: string, open: boolean) => {
    setDropdownStates(prev => ({ ...prev, [key]: open }));
  };

  useEffect(() => {
    if (Object.values(dropdownStates).some(open => open) && containerRef.current) {
      setContainerHeight(containerRef.current.scrollHeight);
    } else {
      setContainerHeight('auto');
    }
  }, [dropdownStates]);

  const onEventIDFilterChanged = value => {
    const filtersCopy = { ...filters };
    filtersCopy['friendlyId'] = value;
    setFilters(filtersCopy);
    setFriendlyId(value);
  };

  const onClearAllFilters = () => {
    setFriendlyId('');
    setEventTypeList([]);
    setTitle('');
    setCategory(null);
    setMachineRelated(null);
    setClusterList([]);
    setRequestingLocationList([]);
    setRequestRegionLocationList([]);
    setLocationList([]);
    setDelivery(null);
    setStartDate('');
    setEndDate('');
    setPriorityList([]);
    setInstructorsNameList([]);
    setInstructorPillarList([]);
    setRolesList([]);
    setEventStatus([]);
    setEventTypeList([]);
    setFilters([]);
    if ((filters || []).length > 0) {
      onClose(false);
    }
  };

  const onEventTypeFilterChanged = (value, fieldName) => {
    const filtersCopy = { ...filters };
    filtersCopy[fieldName] = value;
    setFilters(filtersCopy);
    setEventTypeList(value);
  };

  const onEventTitleFilterChanged = value => {
    const filtersCopy = { ...filters };
    filtersCopy['title'] = value;
    setFilters(filtersCopy);
    setTitle(value);
  };

  const onCategoryChange = (value, fieldName) => {
    const filtersCopy = { ...filters };
    filtersCopy[fieldName] = value;
    setFilters(filtersCopy);
    setCategory(value);
  };

  const onMachineRelatedChange = (value: ItemReference, fieldName: string) => {
    const filtersCopy = { ...filters };
    filtersCopy[fieldName] = value;
    setFilters(filtersCopy);
    setMachineRelated(value);
  };

  const onNmrClusterChange = (value: NMClusterDto[], fieldName: string) => {
    const filtersCopy = { ...filters };
    filtersCopy[fieldName] = value;
    setFilters(filtersCopy);
    setClusterList(value);
  };

  const onRequestingLocationListChange = (value: LocationDto[], fieldName: string) => {
    const filtersCopy = { ...filters };
    filtersCopy[fieldName] = value;
    setFilters(filtersCopy);
    setRequestingLocationList(value);
  };

  const onRequestRegionLocationChange = (value: RegionDto[], fieldName: string) => {
    const filtersCopy = { ...filters };
    filtersCopy[fieldName] = value;
    setFilters(filtersCopy);
    setRequestRegionLocationList(value);
  };

  const handleLocationListFilterChange = (value: LocationDto[], fieldName: string) => {
    const filtersCopy = { ...filters };
    filtersCopy[fieldName] = value;
    setFilters(filtersCopy);
    setLocationList(value);
  };

  const onDeliveryMethodChange = (value: ItemReference) => {
    const filtersCopy = { ...filters };
    filtersCopy['delivery'] = value;
    setFilters(filtersCopy);
    setDelivery(value);
  };

  const startDateFilterChanged = (_, value: string) => {
    const filtersCopy = { ...filters };
    filtersCopy['startDate'] = value;
    setFilters(filtersCopy);
    setStartDate(value);
  };

  const onPriorityListFilterChange = (value: PriorityDto[], fieldName: string) => {
    const filtersCopy = { ...filters };
    filtersCopy[fieldName] = value;
    setFilters(filtersCopy);
    setPriorityList(value);
  };

  const endDateFilterChanged = (_, value: string) => {
    const filtersCopy = { ...filters };
    filtersCopy['endtDate'] = value;
    setFilters(filtersCopy);
    setEndDate(value);
  };

  const onInstructorsListFilterChange = (value: UserDto[], fieldName: string) => {
    const filtersCopy = { ...filters };
    filtersCopy[fieldName] = value;
    setFilters(filtersCopy);
    setInstructorsNameList(value);
  };

  const onInstructorPillarFilterChange = (value: PillarDto[], fieldName: string) => {
    const filtersCopy = { ...filters };
    filtersCopy[fieldName] = value;
    setFilters(filtersCopy);
    setInstructorPillarList(value);
  };

  const onRolesListChange = (value: ProfessionDto[], fieldName: string) => {
    const filtersCopy = { ...filters };
    filtersCopy[fieldName] = value;
    setFilters(filtersCopy);
    setRolesList(value);
  };

  const onStatusFilterChange = (value: EventStatus[], fieldName: string) => {
    const filtersCopy = { ...filters };
    filtersCopy[fieldName] = value;
    setFilters(filtersCopy);
    setEventStatus(value);
  };

  const onSaveClickHandler = () => {
    onSave(filters);
  };

  return (
    <>
      <div ref={containerRef} style={{ height: containerHeight, transition: 'height 0.3s' }} className="overflow-y-auto bg-white p-4">
        <button onClick={() => onClose(true)} className="flex items-center gap-1 text-sm text-black-600 mb-2">
          ← Back
        </button>
        <h3 className="text-lg font-semibold mb-4">Filters</h3>
        <div className="mb-3">
          <label className="block text-sm text-gray-700 mb-1">{t('Event ID')}</label>
          <TailwindClearableTimerInput
            onKeyPress={e => e.key === 'Enter' && e.preventDefault()}
            className="w-full p-2 border border-gray-300 rounded"
            placeholder={t('Event ID')}
            onChange={onEventIDFilterChanged}
            value={friendlyId}
          />
        </div>
        <div className="mb-3">
          <label className="block text-sm text-gray-700 mb-1">Event Type</label>
          <TailwindMultiEventTypeEditor
            className="w-full p-2 border border-gray-300 rounded"
            schedulerRender
            placeholder={t('Event Type')}
            useOriginalEventTypeIdAsValue
            value={eventTypeList.map(eTI => eTI)}
            onChange={value => onEventTypeFilterChanged(value, 'eventType')}
            loadDataOnOpen={true}
            onFocus={() => handleDropdownOpen('eventType', true)}
            onBlur={() => handleDropdownOpen('eventType', false)}
          />
        </div>
        <div className="mb-3">
          <label className="block text-sm text-gray-700 mb-1">{t('Event Title')}</label>
          <TailwindClearableTimerInput
            onKeyPress={e => e.key === 'Enter' && e.preventDefault()}
            className="w-full p-2 border border-gray-300 rounded"
            placeholder={t('Event Tittle')}
            onChange={onEventTitleFilterChanged}
            value={title}
          />
        </div>
        <div className="mb-3">
          <label className="block text-sm text-gray-700 mb-1">{t('Category')}</label>
          <TailwindCategoryFormEditor
            readOnly={false}
            className="w-full p-2 border border-gray-300 rounded"
            value={category}
            onChange={value => onCategoryChange(value, 'category')}
            clearable
            placeholder={t('Category')}
            onFocus={() => handleDropdownOpen('category', true)}
            onBlur={() => handleDropdownOpen('category', false)}
          />
        </div>
        <div className="mb-3">
          <label className="block text-sm text-gray-700 mb-1">{t('Machine Related')}</label>
          <TailwindMachineRelatedEditor
            placeholder={t('Machine Related')}
            className="w-full p-2 border border-gray-300 rounded"
            nullable
            value={machineRelated}
            onChange={value => onMachineRelatedChange(value, 'machineRelated')}
            onFocus={() => handleDropdownOpen('machineRelated', true)}
            onBlur={() => handleDropdownOpen('machineRelated', false)}
          />
        </div>
        <div className="mb-3">
          <label className="block text-sm text-gray-700 mb-1">{t('Cluster NMR')}</label>
          <TailwindMultipleNmrClustersEditor
            className="w-full p-2 border border-gray-300 rounded"
            category={category}
            placeholder={t('Cluster NMR')}
            value={clusterList}
            onChange={value => onNmrClusterChange(value, 'nmrCluster')}
            loadDataOnOpen={true}
            onFocus={() => handleDropdownOpen('nmrCluster', true)}
            onBlur={() => handleDropdownOpen('nmrCluster', false)}
          />
        </div>
        <div className="mb-3">
          <label className="block text-sm text-gray-700 mb-1">{t('Requesting Location')}</label>
          <TailwindMultipleLocationEditor
            className="w-full p-2 border border-gray-300 rounded"
            clearable
            placeholder={t('Requesting Location')}
            value={requestingLocationList}
            onChange={value => onRequestingLocationListChange(value, 'requests')}
            showCountry={true}
            loadDataOnOpen={true}
            onFocus={() => handleDropdownOpen('requests', true)}
            onBlur={() => handleDropdownOpen('requests', false)}
          />
        </div>
        <div className="mb-3">
          <label className="block text-sm text-gray-700 mb-1">{t('Request Location Region')}</label>
          <TailwindMultipleRegionEditor
            clearable
            placeholder={t('Request Location Region')}
            value={requestRegionLocationList}
            onChange={value => onRequestRegionLocationChange(value, 'extendedRequests')}
            className="events-home-page__multiple-dropdown-filter"
            loadDataOnOpen={true}
            onFocus={() => handleDropdownOpen('extendedRequests', true)}
            onBlur={() => handleDropdownOpen('extendedRequests', false)}
          />
        </div>
        <div className="mb-3">
          <label className="block text-sm text-gray-700 mb-1">{t('Event Location')}</label>
          <TailwindMultipleLocationEditor
            className="w-full p-2 border border-gray-300 rounded"
            clearable
            placeholder={t('Event Location')}
            value={locationList}
            onChange={value => handleLocationListFilterChange(value, 'eventLocation')}
            showCountry={true}
            loadDataOnOpen={true}
            onFocus={() => handleDropdownOpen('eventLocation', true)}
            onBlur={() => handleDropdownOpen('eventLocation', false)}
          />
        </div>
        <div className="mb-3">
          <label className="block text-sm text-gray-700 mb-1">{t('Delivery Method')}</label>
          <TailwindDeliveryEditor
            className="w-full p-2 border border-gray-300 rounded"
            clearable
            placeholder={t('Delivery Method')}
            value={delivery}
            onChange={value => onDeliveryMethodChange(value)}
            loadDataOnOpen={true}
            onFocus={() => handleDropdownOpen('delivery', true)}
            onBlur={() => handleDropdownOpen('delivery', false)}
          />
        </div>
        <div className="mb-3">
          <label className="block text-sm text-gray-700 mb-1">{t('Start Date')}</label>
          <DateInput
            className="w-full p-2 border border-gray-300 rounded"
            value={startDate || null}
            onChange={startDateFilterChanged}
            placeholder={t('Start Date')}
            clearable
          />
        </div>
        <div className="mb-3">
          <label className="block text-sm text-gray-700 mb-1">{t('End Date')}</label>
          <DateInput
            placeholder={t('End Date')}
            className="w-full p-2 border border-gray-300 rounded"
            value={endDate || null}
            onChange={endDateFilterChanged}
            initialValue={startDate || null}
            minDate={startDate || null}
            clearable
          />
        </div>
        <div className="mb-3">
          <label className="block text-sm text-gray-700 mb-1">{t('Priority')}</label>
          <TailwindMultiplePriorityEditor
            className="w-full p-2 border border-gray-300 rounded"
            placeholder={t('Priority')}
            value={priorityList}
            clearable
            onChange={value => onPriorityListFilterChange(value, 'priority')}
            loadDataOnOpen={true}
            onFocus={() => handleDropdownOpen('priority', true)}
            onBlur={() => handleDropdownOpen('priority', false)}
          />
        </div>
        <div className="mb-3">
          <label className="block text-sm text-gray-700 mb-1">{t('Instructor Name')}</label>
          <TailwindMultipleUsersEditor
            className="w-full p-2 border border-gray-300 rounded"
            placeholder={t('Instructor Name')}
            value={instructorsNameList}
            clearable
            onChange={value => onInstructorsListFilterChange(value, 'instructors')}
            role="Instructor"
            locationIds={[]}
            loadDataOnOpen={true}
            onFocus={() => handleDropdownOpen('instructors', true)}
            onBlur={() => handleDropdownOpen('instructors', false)}
          />
        </div>
        <div className="mb-3">
          <label className="block text-sm text-gray-700 mb-1">{t("Instructor's Pillar")}</label>
          <TailwindMultiplePillarsEditor
            className="w-full p-2 border border-gray-300 rounded"
            placeholder={t("Instructor's Pillar")}
            value={instructorPillarList}
            clearable
            onChange={pillars => onInstructorPillarFilterChange(pillars, 'instructorsPillars')}
            loadDataOnOpen={true}
            onFocus={() => handleDropdownOpen('instructorsPillars', true)}
            onBlur={() => handleDropdownOpen('instructorsPillars', false)}
          />
        </div>
        <div className="mb-3">
          <label className="block text-sm text-gray-700 mb-1">{t('Roles')}</label>
          <TailwindMultipleProfessionsEditor
            clearable
            className="w-full p-2 border border-gray-300 rounded"
            placeholder={t('Roles')}
            value={rolesList}
            onChange={value => onRolesListChange(value, 'profession')}
            loadDataOnOpen={true}
            onFocus={() => handleDropdownOpen('profession', true)}
            onBlur={() => handleDropdownOpen('profession', false)}
          />
        </div>
        <div className="mb-3">
          <label className="block text-sm text-gray-700 mb-1">{t('Status')}</label>
          <TailwindMultipleEventStatusEditor
            clearable
            statusFilterActive={true}
            placeholder={t('Status')}
            value={eventStatus}
            onChange={value => onStatusFilterChange(value, 'EventStatus')}
            onFocus={() => handleDropdownOpen('EventStatus', true)}
            onBlur={() => handleDropdownOpen('EventStatus', false)}
          />
        </div>
      </div>

      <div
        style={{
          position: 'fixed',
          bottom: 0,
          left: 58,
          right: 11,
          background: 'white',
          borderTop: '1px solid #ddd',
          padding: '0.5rem 1rem',
          zIndex: 1000
        }}
        className="flex gap-2"
      >
        <button className="flex-1 bg-primary-600 text-white py-2 rounded" onClick={() => onSaveClickHandler()}>
          Apply filters
        </button>
        <button className="flex-1 bg-primary-600 text-white py-2 rounded" onClick={() => onClearAllFilters()}>
          Clear all filters
        </button>
      </div>
    </>
  );
};

export default EventListFilters;
